import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";

import { getCustomerById } from "../../../services/clearing/customers";
import { useCustomerAnalytics } from "../../../hooks/useCustomerAnalytics";
import { AnalyticsPeriod } from "../../../types/analytics";

import PeriodSelector from "./components/PeriodSelector";
import StationFilter from "./components/StationFilter";
import KpiRow from "./components/KpiRow";
import RevenueTrendChart from "./components/RevenueTrendChart";
import SessionsEnergyChart from "./components/SessionsEnergyChart";
import StationComparisonChart from "./components/StationComparisonChart";
import ProviderStatsPanel from "./components/ProviderStatsPanel";
import UserStatsPanel from "./components/UserStatsPanel";

// Import chart config to register Chart.js plugins
import "./components/chartConfig";

const CustomerStatisticsPage: React.FC = () => {
  const { id: customerId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [customerName, setCustomerName] = useState<string>("");
  const [period, setPeriod] = useState<AnalyticsPeriod>(6);
  const [selectedStationIds, setSelectedStationIds] = useState<
    string[] | null
  >(null);

  const { stationIds, reportData, detailData, loading, detailLoading, error } =
    useCustomerAnalytics(customerId, period, selectedStationIds);

  useEffect(() => {
    if (customerId) {
      getCustomerById(customerId)
        .then((customer: any) => {
          setCustomerName(
            customer?.address?.company_name ||
              customer?.account_holder_name ||
              customerId
          );
        })
        .catch(() => setCustomerName(customerId || ""));
    }
  }, [customerId]);

  const isControlsDisabled = loading || detailLoading;

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mb-4">
        <div className="flex align-items-center gap-3">
          <Button
            icon="pi pi-arrow-left"
            className="p-button-text p-button-rounded"
            onClick={() => navigate(`/customers/${customerId}`)}
            tooltip="Back to Customer"
          />
          <div>
            <h2 className="m-0">{customerName}</h2>
            <small className="text-500">Revenue Statistics</small>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mb-4">
        <PeriodSelector
          value={period}
          onChange={setPeriod}
          disabled={isControlsDisabled}
        />
        {stationIds.length > 1 && (
          <StationFilter
            stationIds={stationIds}
            selected={selectedStationIds}
            onChange={setSelectedStationIds}
            disabled={isControlsDisabled}
          />
        )}
      </div>

      {/* Error state */}
      {error && (
        <Message severity="error" text={error} className="w-full mb-4" />
      )}

      {/* Loading state */}
      {loading && !error && (
        <div className="flex justify-content-center p-6">
          <ProgressSpinner />
        </div>
      )}

      {/* Empty state: no stations */}
      {!loading && !error && stationIds.length === 0 && (
        <div className="flex flex-column align-items-center justify-content-center text-center p-6">
          <i className="pi pi-building text-6xl text-300 mb-3" />
          <h3 className="text-500">No Stations Assigned</h3>
          <p className="text-500">
            This customer has no stations assigned. Assign stations first to see
            statistics.
          </p>
          <Button
            label="Go to Customer"
            icon="pi pi-arrow-left"
            className="p-button-outlined mt-3"
            onClick={() => navigate(`/customers/${customerId}`)}
          />
        </div>
      )}

      {/* Data loaded */}
      {!loading && !error && reportData && reportData.totalSessions > 0 && (
        <>
          {/* KPI Row */}
          <KpiRow
            reportData={reportData}
            detailData={detailData}
            detailLoading={detailLoading}
          />

          {/* Charts Row 1: Revenue Trend + Sessions & Energy */}
          <div className="grid mb-4">
            <div className="col-12 md:col-6">
              <RevenueTrendChart monthly={reportData.monthly} />
            </div>
            <div className="col-12 md:col-6">
              <SessionsEnergyChart
                monthly={reportData.monthly}
                hasDetailData={!!detailData}
              />
            </div>
          </div>

          {/* Charts Row 2: Provider Stats + Station Comparison (only when multiple stations) */}
          <div className="grid mb-4">
            <div className={`col-12${reportData.stations.length > 1 ? " md:col-6" : ""}`}>
              <ProviderStatsPanel
                providers={detailData?.providers || null}
                loading={detailLoading}
              />
            </div>
            {reportData.stations.length > 1 && (
              <div className="col-12 md:col-6">
                <StationComparisonChart stations={reportData.stations} />
              </div>
            )}
          </div>

          {/* User Stats Panel */}
          <div className="mb-4">
            <UserStatsPanel detailData={detailData} loading={detailLoading} />
          </div>
        </>
      )}

      {/* Empty state: no data for period */}
      {!loading &&
        !error &&
        stationIds.length > 0 &&
        reportData &&
        reportData.totalSessions === 0 && (
          <div className="flex flex-column align-items-center justify-content-center text-center p-6">
            <i className="pi pi-chart-line text-6xl text-300 mb-3" />
            <h3 className="text-500">No Data for Selected Period</h3>
            <p className="text-500">
              Try selecting a different time period or check if the stations have
              any charging sessions.
            </p>
          </div>
        )}
    </div>
  );
};

export default CustomerStatisticsPage;
